import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { AllocationInputQuestion as AllocationInputQuestionType, AllocationField } from "./questionTypes";
import { QuestionAnswer } from "./answerTypes";

interface AllocationInputQuestionProps {
  question: AllocationInputQuestionType;
  value: Record<string, number>;
  onChange: (questionId: string, value: QuestionAnswer) => void;
}

export function AllocationInputQuestion({ question, value, onChange }: AllocationInputQuestionProps) {
  // Allocation input renderer — renders percentage fields with running total validation
  const fields = useMemo(() => {
    if (!question.options || !Array.isArray(question.options)) return [];
    return question.options as unknown as AllocationField[];
  }, [question.options]);

  const [values, setValues] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    for (const f of fields) {
      initial[f.field] = value?.[f.field] ?? 0;
    }
    return initial;
  });

  // Sync from parent when value changes externally
  useEffect(() => {
    if (value && typeof value === 'object') {
      setValues(prev => {
        const next: Record<string, number> = {};
        let changed = false;
        for (const f of fields) {
          const v = value[f.field] ?? 0;
          next[f.field] = v;
          if (prev[f.field] !== v) changed = true;
        }
        return changed ? next : prev;
      });
    }
  }, [value, fields]);

  const total = useMemo(() => {
    return Object.values(values).reduce((sum, v) => sum + (v || 0), 0);
  }, [values]);

  const remaining = 100 - total;
  const isValid = Math.abs(total - 100) < 0.01;
  const isOver = total > 100;

  const handleFieldChange = (field: string, raw: string) => {
    // Allow empty, integers, and one decimal place
    if (raw === '') {
      const next = { ...values, [field]: 0 };
      setValues(next);
      onChange(question.id, next as unknown as QuestionAnswer);
      return;
    }
    
    const match = raw.match(/^\d{0,3}(\.\d{0,1})?$/);
    if (!match) return;
    
    const num = parseFloat(raw);
    if (isNaN(num) || num < 0 || num > 100) return;

    const next = { ...values, [field]: num };
    setValues(next);
    onChange(question.id, next as unknown as QuestionAnswer);
  };

  const totalColor = isValid
    ? "text-green-600 dark:text-green-400"
    : isOver
      ? "text-destructive"
      : "text-muted-foreground";

  return (
    <div className="space-y-6 w-full max-w-2xl">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">{question.title}</h2>
        {question.description && (
          <p className="text-muted-foreground text-sm">{question.description}</p>
        )}
      </div>

      <div className="space-y-4">
        {fields.map((f) => (
          <div key={f.field} className="flex items-center gap-4">
            <label className="flex-1 text-sm font-medium text-foreground min-w-0">
              {f.label}
            </label>
            <div className="relative w-28">
              <Input
                type="text"
                inputMode="decimal"
                value={values[f.field] === 0 ? '' : values[f.field].toString()}
                onChange={(e) => handleFieldChange(f.field, e.target.value)}
                placeholder="0"
                className="pr-8 text-right"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">
                %
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Running total */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
        isValid ? "border-green-500/30 bg-green-50 dark:bg-green-950/20" 
        : isOver ? "border-destructive/30 bg-destructive/5" 
        : "border-border bg-muted/50"
      }`}>
        <span className={`text-sm font-semibold ${totalColor}`}>
          Total: {total}%
        </span>
        <span className={`text-sm ${totalColor}`}>
          {isValid
            ? "✓ Adds up to 100%"
            : isOver
              ? `${(total - 100).toFixed(1)}% over — please reduce`
              : `${remaining.toFixed(1)}% remaining`}
        </span>
      </div>

      {!isValid && (
        <p className="text-sm text-destructive">
          Revenue percentages must total 100%
        </p>
      )}
    </div>
  );
}
